import { sanitizeReceiptHtml } from './receipt-html'

const PRINT_FRAME_WIDTH_PX = 1100
// Some browsers never fire afterprint from a frame; don't leak it if that happens.
const CLEANUP_FALLBACK_MS = 60_000

const waitForImages = (doc: Document): Promise<void> => {
  const pending = Array.from(doc.images).filter((image) => !image.complete)
  if (pending.length === 0) return Promise.resolve()
  return Promise.all(
    pending.map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true })
          image.addEventListener('error', () => resolve(), { once: true })
        }),
    ),
  ).then(() => undefined)
}

/**
 * Opens the browser's own print dialog on the receipt alone. Unlike the PDF path this
 * keeps text as text — the receipt's @media print rules apply, and "Save as PDF" from
 * the dialog produces a vector document rather than a screenshot.
 */
export const printReceiptDocument = async (receiptHtml: string): Promise<boolean> => {
  const frame = document.createElement('iframe')
  frame.style.position = 'absolute'
  frame.style.left = '-10000px'
  frame.style.top = '0'
  frame.style.width = `${PRINT_FRAME_WIDTH_PX}px`
  frame.style.height = '1200px'
  document.body.appendChild(frame)

  const doc = frame.contentDocument
  const win = frame.contentWindow
  if (!doc || !win) {
    frame.remove()
    return false
  }

  doc.open()
  // Sanitized rather than sandboxed: a sandbox without allow-modals blocks print().
  doc.write(
    `<!doctype html><html><head><title>Receipt</title></head><body>${sanitizeReceiptHtml(receiptHtml)}</body></html>`,
  )
  doc.close()

  await waitForImages(doc)

  const cleanup = () => frame.remove()
  win.addEventListener('afterprint', cleanup, { once: true })
  window.setTimeout(cleanup, CLEANUP_FALLBACK_MS)

  win.focus()
  win.print()
  return true
}
